import { CURRENT_CLIENT } from '@/constants'
import type {
  ListFilterId,
  ListFilterLayout,
  ListFilterLayoutConfig,
  PlatformListFilterId,
} from '@/components/filters/list-filter-types'

/**
 * Per-list leading scope, primary (additive `+ Dimension` pills), and secondary (funnel menu) layout.
 *
 * Convention: ≤3 filter dimensions → all primary, no funnel. 4+ → curate 2–3 primary, rest secondary.
 * See docs/DESIGN_SYSTEM.md § List filters.
 */
const DEFAULT_LAYOUTS: Record<PlatformListFilterId, ListFilterLayout> = {
  candidateJobs: {
    primary: ['skills', 'work_mode', 'employment_type', 'posted'],
    secondary: ['source', 'location', 'organisation'],
  },
  candidateMarketplace: {
    primary: ['location'],
    secondary: [],
  },
  employerJobs: {
    primary: ['publication_status', 'work_mode', 'employment_type'],
    secondary: [],
  },
  employerJobApplicants: {
    primary: ['skills', 'location', 'company'],
    secondary: [],
  },
  employerTalent: {
    primary: ['skills', 'opportunity_id'],
    secondary: [],
  },
  recruiterTalent: {
    primary: ['skills', 'countries', 'cities'],
    secondary: [],
  },
  memories: {
    primary: ['category', 'importance'],
    secondary: [],
  },
  adminUsers: {
    lead: 'group',
    primary: [],
    secondary: [],
  },
  adminOrganisations: {
    primary: [],
    secondary: [],
  },
  adminWaitlist: {
    lead: 'status',
    primary: [],
    secondary: [],
  },
  adminFeedback: {
    lead: 'kind',
    primary: [],
    secondary: [],
  },
  feedback: {
    lead: 'kind',
    primary: [],
    secondary: [],
  },
  recruiterBriefs: {
    lead: 'status',
    primary: [],
    secondary: [],
  },
  callEvaluations: {
    lead: 'status',
    primary: [],
    secondary: [],
  },
}

function getConfigOverrides(): ListFilterLayoutConfig | undefined {
  return (CURRENT_CLIENT as { layout?: { listFilterLayouts?: ListFilterLayoutConfig } }).layout
    ?.listFilterLayouts
}

const EMPTY_LAYOUT: ListFilterLayout = { primary: [], secondary: [] }

/** `fallback` is the default for lists the platform does not know (pack lists). */
export function resolveListFilterLayout(
  listId: ListFilterId,
  fallback: ListFilterLayout = EMPTY_LAYOUT,
): ListFilterLayout {
  const defaults = (DEFAULT_LAYOUTS as Partial<Record<string, ListFilterLayout>>)[listId] ?? fallback
  const override = getConfigOverrides()?.[listId]
  if (!override) return defaults
  return {
    lead: override.lead ?? defaults.lead,
    primary: override.primary ?? defaults.primary,
    secondary: override.secondary ?? defaults.secondary,
  }
}

/** All filter keys for the in-field menu (lead scope is intentionally excluded). */
export function listFilterMenuKeys(layout: ListFilterLayout): string[] {
  return [...layout.primary, ...layout.secondary]
}

